"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "ice-maker-size-calculator",
  "ice-maker-energy-cost-calculator",
  "refrigerator-size-calculator",
  "energy-cost-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function IceMakerCapacityCalculatorPageClient() {
  const meta = TOOL_METADATA["ice-maker-capacity-calculator"]

  const [people, setPeople] = useState("4")
  const [usage, setUsage] = useState("normal")
  const [entertaining, setEntertaining] = useState("occasional")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const numPeople = readCount(people, { min: 1, max: 50 })
    if (numPeople === null) return null

    let dailyBase = numPeople * 2
    if (usage === "heavy") dailyBase *= 2
    else if (usage === "light") dailyBase *= 0.5

    if (entertaining === "frequent") dailyBase *= 1.5
    else if (entertaining === "occasional") dailyBase *= 1.2

    const dailyNeeds = Math.round(dailyBase * 10) / 10

    let recommendation: string
    let iceType: string
    if (dailyNeeds <= 3) {
      recommendation = "Standard ice maker (3–5 lbs/day) will meet your needs"
      iceType = "Standard cubes"
    } else if (dailyNeeds <= 6) {
      recommendation = "High-capacity ice maker (6–8 lbs/day) recommended"
      iceType = "Standard or craft ice"
    } else if (dailyNeeds <= 10) {
      recommendation = "Premium ice maker (8–12 lbs/day) or dual ice makers"
      iceType = "Craft ice or nugget ice"
    } else {
      recommendation = "Consider an additional ice maker or commercial-grade unit"
      iceType = "Multiple ice types"
    }

    return { dailyNeeds, recommendation, iceType }
  }, [people, usage, entertaining])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="How many pounds of ice"
      heroTitleItalic="per day"
      heroTitlePost="for your house?"
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Foodservice consumption rates"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "2 lbs", label: "Per person / day", note: "Baseline" },
            { value: "+50%", label: "Frequent entertaining", note: "Modeled" },
            { value: "50%", label: "Bin / daily ratio", note: "Sizing rule" },
            { value: "3–5", label: "Lbs from fridge", note: "Built-in" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "ice-maker-capacity-calculator")}
      cta={{
        title: "More ice than your fridge can make.",
        body: "Browse undercounter and countertop ice makers sized to fit kitchens, bars, and outdoor spaces.",
        buttonLabel: "Browse ice makers",
        buttonHref: "/ice-makers",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Household.
                <br />
                Use pattern.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Built-in fridge ice makers cap at ~5 lbs/day — frequent entertaining usually exceeds that.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Household size
                  </label>
                  <select
                    value={people}
                    onChange={(e) => setPeople(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="1">1 person</option>
                    <option value="2">2 people</option>
                    <option value="3">3 people</option>
                    <option value="4">4 people</option>
                    <option value="5">5 people</option>
                    <option value="6">6+ people</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Ice usage pattern
                  </label>
                  <select
                    value={usage}
                    onChange={(e) => setUsage(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="light">Light — occasional drinks</option>
                    <option value="normal">Normal — daily drinks, some cooking</option>
                    <option value="heavy">Heavy — multiple drinks, frequent cooking</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Entertaining frequency
                  </label>
                  <select
                    value={entertaining}
                    onChange={(e) => setEntertaining(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="rarely">Rarely</option>
                    <option value="occasional">Occasionally (monthly)</option>
                    <option value="frequent">Frequently (weekly)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Entertaining drives peak demand.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Daily ice need
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.dailyNeeds ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">lbs/day</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendation ?? "Pick household size to begin."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Best ice type</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.iceType ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Storage bin</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">
                        {result ? Math.ceil(result.dailyNeeds * 0.5) : "—"} lbs
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Or upgrade the fridge"
          title="Bottom-freezer fridges with built-in ice makers"
          description="Modern bottom-freezer and French door units produce 5–10 lbs/day with dispensers."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Foodservice rates. <em className="italic text-[#ff9951]">Real</em> bins.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Per-person consumption rates draw on residential and foodservice industry sizing guidance from manufacturers like Hoshizaki and Manitowoc.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Baseline daily need",
                  d: "2 lbs of ice per person per day. Light use halves it; heavy use doubles it.",
                },
                {
                  n: "02",
                  t: "Entertaining adjustment",
                  d: "Occasional entertaining adds 20%; frequent entertaining adds 50% on top of baseline.",
                },
                {
                  n: "03",
                  t: "Storage bin sizing",
                  d: "Plan for ~50% of daily production in bin storage so ice rotates and doesn't fuse.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door fridges with dual ice makers"
          description="Top-tier ice configurations for high-volume households — exterior dispenser plus dedicated freezer ice maker."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
